import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthContext } from "@mavenmm/teamwork-auth";
import type { AssistantType } from "../../types/assistant";
import { useAssistantAuth } from "../../hooks/useAssistantAuth";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useGraphQL } from "../../hooks/useGraphQL";
import { useSpacesFilters } from "../../hooks/useSpacesFilters";
import { useMetadataFilters } from "../../hooks/useMetadataFilters";
import { useAvailableModels } from "../../hooks/useAvailableModels";
import { SpacesFilterPanel } from "../SpacesFilterPanel";
import { MetadataFilterPanel } from "../MetadataFilterPanel";
import { SpacesFilterButton } from "../SpacesFilterButton";
import { AssistantTopBar, CORPUS_OPTIONS } from "./AssistantTopBar";
import { AssistantChannel } from "./AssistantChannel";
import { AssistantRail } from "./AssistantRail";
import { HistorySidebar } from "./HistorySidebar";
import { loadThread } from "./historyApi";
import { modelsInThread, restoreThread } from "./restoreThread";
import type { ChatMessage } from "../../types/assistant";
import { addChannel, canAddChannel, MAX_CHANNELS, removeChannel, setChannelModel, type Channel } from "./channels";
import { makeLogEntry, type TurnLogEntry } from "./turnLog";
import { useDefaultModel } from "../../hooks/useDefaultModel";
import { useAssistantAccess } from "../../hooks/useAssistantAccess";
import { DEFAULT_MODEL_ID, SECOND_MODEL_ID } from "../../lib/models";

/**
 * The whole assistant, in one place, shared by the home-page modal and /assistant.
 *
 * One prompt can be answered by up to three models at once. Each channel is a fully
 * independent turn — its own socket, its own history — which is what makes them
 * comparable, and why the composer lives here rather than inside any one column.
 */

const GET_RESTRICTED_CLIENT_IDS = `
  query GetUserRestrictedClientIds($userId: String!) {
    getUserRestrictedClientIds(userId: $userId) {
      clientIds
    }
  }
`;

export function AssistantSurface({
  onClose,
  enabled = true,
  enableComparison = false,
}: {
  onClose?: () => void;
  enabled?: boolean;
  /**
   * Whether this surface offers multi-model comparison. Off by default, so the
   * home-page modal stays the single-model chat everyone already knows; the
   * /assistant page opts in. Being an evaluation tool, the comparison belongs on
   * the surface built for it rather than in the assistant people use daily.
   */
  enableComparison?: boolean;
}) {
  const { user, isAuthenticated, getAccessToken } = useAuthContext();
  const { user: currentUser } = useCurrentUser();
  const { authToken, authError, isLoading: authLoading } = useAssistantAuth({ enabled });
  const { models } = useAvailableModels();
  // Two gates, both of which must pass: the surface has to offer comparison at all,
  // and the person has to be one of the testers.
  const { canCompareModels } = useAssistantAccess();
  const showComparison = enableComparison && canCompareModels;
  // The home-page modal keeps the layout it always had: a plain header, and the
  // corpus switcher directly above the composer. The /assistant workbench hoists
  // that into the top bar beside the comparison controls.
  const classicLayout = !enableComparison;

  const [assistantType, setAssistantType] = useState<AssistantType>("spaces");
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const { defaultModel } = useDefaultModel();
  // The workbench exists to compare, so it opens with two columns. The home modal
  // is a single conversation and opens with one.
  const [channels, setChannels] = useState<Channel[]>(() =>
    enableComparison
      ? [
          { id: "ch-0", modelId: DEFAULT_MODEL_ID },
          { id: "ch-1", modelId: SECOND_MODEL_ID },
        ]
      : [{ id: "ch-0", modelId: DEFAULT_MODEL_ID }],
  );
  const [log, setLog] = useState<TurnLogEntry[]>([]);
  const [showSidebar, setShowSidebar] = useState(true);
  // Conversation history is parked. The sidebar, its endpoints and its tests are all
  // here and working, but reopening a thread only restores what was said — the Claude
  // path has no conversation memory, so continuing a restored thread would answer as
  // though the transcript above it did not exist. Turning this on before that is
  // fixed would make history look broken rather than absent.
  const HISTORY_ENABLED = false;
  const [showHistory, setShowHistory] = useState(false);
  // Bumped when a turn is persisted so the history list refreshes without a reload.
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [restored, setRestored] = useState<Record<string, ChatMessage[]>>({});
  const [draft, setDraft] = useState("");
  // PAAB rebuttal mode, from the original modal. It builds one formatted prompt out
  // of two verbatim fields rather than asking people to paste both into the composer.
  const [projectInputMode, setProjectInputMode] = useState<"general" | "rebuttal">("general");
  const [paabComment, setPaabComment] = useState("");
  const [copyDeckText, setCopyDeckText] = useState("");

  // The saved default arrives after the first render (it needs the profile and the
  // catalogue). Adopt it once, and only while the surface is still untouched — never
  // yank a model out from under a conversation in progress.
  const adoptedDefault = useRef(false);
  useEffect(() => {
    // A saved default is a tester's pick, made on /assistant. Home stays on the
    // production model until one of them is chosen as the winner.
    if (classicLayout) return;
    if (adoptedDefault.current || !defaultModel) return;
    if (channels.length !== 1 || log.length > 0) return;
    adoptedDefault.current = true;
    setChannels([{ id: "ch-0", modelId: defaultModel }]);
  }, [classicLayout, defaultModel, channels.length, log.length]);

  const spacesFilters = useSpacesFilters();

  // Asked of the Dashboard API as the signed-in person (graphql-proxy). PAAB
  // Hasura stopped serving this lookup on 2026-10-07 (paab-hasura#14).
  const {
    data: restrictedClientData,
    errors: restrictedClientErrors,
    execute: executeRestrictedClients,
  } = useGraphQL<{
    getUserRestrictedClientIds: { clientIds: string[] } | null;
  }>();

  useEffect(() => {
    if (user?.id) executeRestrictedClients(GET_RESTRICTED_CLIENT_IDS, { userId: String(user.id) });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The ethical wall. An empty list means "no restrictions", so a failed or
  // malformed lookup must not fall back to one: Past Projects and PAAB
  // Correspondence wait for "ready" (Spaces doesn't filter by client).
  const restrictedClientIds = restrictedClientData?.getUserRestrictedClientIds?.clientIds;
  const clientAccess: "loading" | "ready" | "unavailable" = restrictedClientErrors
    ? "unavailable"
    : Array.isArray(restrictedClientIds)
      ? "ready"
      : restrictedClientData
        ? "unavailable"
        : "loading";
  const excludedClientIds = restrictedClientIds ?? [];
  const metadataFilters = useMetadataFilters(excludedClientIds);

  const userName = isAuthenticated && user?.firstName ? user.firstName : "User";
  const userEmail = currentUser?.email_address || user?.email || "";

  const fileSearchFilters =
    assistantType === "spaces" ? spacesFilters.fileSearchFilters : metadataFilters.fileSearchFilters;
  const activeFilterCount =
    assistantType === "spaces" ? spacesFilters.activeFilterCount : metadataFilters.activeFilterCount;

  // Each channel registers its send here so one composer can reach all of them.
  const sendersRef = useRef(new Map<string, (text: string) => void>());
  const registerSend = useCallback((id: string, send: ((text: string) => void) | null) => {
    if (send) sendersRef.current.set(id, send);
    else sendersRef.current.delete(id);
  }, []);

  // One conversation id for the whole surface, shared by every channel, so all the
  // models answering a prompt land in the same thread and reopening it shows the
  // comparison rather than one arbitrary column.
  const conversationIdRef = useRef<string>(crypto.randomUUID());

  const recordTurn = useCallback(
    (channelId: string, requestedModel: string, info: Parameters<typeof makeLogEntry>[0] extends never ? never : {
      model?: string;
      provider?: "openai" | "anthropic";
      inputTokens?: number;
      outputTokens?: number;
      elapsedMs: number;
      prompt?: string;
      reply?: string;
      annotations?: unknown[];
    }) => {
      // Fire-and-forget: history is a convenience, and a failure here must never
      // break the chat that produced it.
      if (info.reply?.trim()) {
        const token = getAccessToken();
        if (token) {
          void fetch("/api/saveConversation", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              conversationId: conversationIdRef.current,
              assistantType,
              messages: [
                ...(info.prompt ? [{ role: "user", content: info.prompt }] : []),
                {
                  role: "assistant",
                  content: info.reply,
                  annotations: info.annotations ?? [],
                  model: info.model,
                  provider: info.provider,
                  channelId,
                  usage: { inputTokens: info.inputTokens, outputTokens: info.outputTokens },
                },
              ],
            }),
          })
            .then((res) => { if (res.ok) setHistoryRefresh((n) => n + 1); })
            .catch(() => {});
        }
      }

      setLog((prev) => [
        makeLogEntry({
          channelId,
          requestedModel,
          servedModel: info.model,
          provider: info.provider,
          inputTokens: info.inputTokens,
          outputTokens: info.outputTokens,
          elapsedMs: info.elapsedMs,
        }),
        ...prev,
      ]);
    },
    [assistantType, getAccessToken],
  );

  /**
   * Reopen a saved thread: rebuild the columns that answered it, then drop each
   * transcript into its own column. Rebuilding the columns first matters — restoring
   * a two-model comparison into one column would silently merge two models' replies
   * into one stream.
   */
  const openThread = async (conversationId: string) => {
    const token = getAccessToken();
    if (!token) return;
    setActiveConversationId(conversationId);
    try {
      const { messages: stored } = await loadThread(token, conversationId);
      const answered = modelsInThread(stored);

      const rebuilt: Channel[] =
        answered.length > 0
          ? answered.slice(0, MAX_CHANNELS).map(({ channelId, modelId }) => ({ id: channelId, modelId }))
          : channels;

      setChannels(rebuilt);
      setRestored(restoreThread(stored, rebuilt.map((c) => c.id)));
      // Continuing a reopened thread appends to it rather than starting a new one.
      conversationIdRef.current = conversationId;
      setLog([]);
    } catch (e) {
      console.error("[history] could not open thread", e);
    }
  };

  /** Same prompt, every channel, same moment — the comparison depends on it. */
  const sendToAll = (text: string) => {
    for (const send of sendersRef.current.values()) send(text);
  };

  const broadcast = () => {
    const text = draft.trim();
    if (!text) return;
    sendToAll(text);
    setDraft("");
  };

  const submitRebuttal = () => {
    if (!paabComment.trim() || !copyDeckText.trim()) return;
    sendToAll(`PAAB Comment:\n${paabComment.trim()}\n\nCopy Deck Text:\n${copyDeckText.trim()}`);
    setPaabComment("");
    setCopyDeckText("");
  };

  // Remounting every channel is how "New Chat" clears the transcripts.
  const handleNewChat = () => {
    setChannels((prev) => prev.map((c, i) => ({ ...c, id: `ch-${Date.now()}-${i}` })));
    setLog([]);
    conversationIdRef.current = crypto.randomUUID();
    setActiveConversationId(null);
    setRestored({});
  };

  const multi = channels.length > 1;

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-maven-gray-800">
      <AssistantTopBar
        assistantType={assistantType}
        setAssistantType={setAssistantType}
        onNewChat={handleNewChat}
        onClose={onClose}
        canAddChannel={canAddChannel(channels)}
        onAddChannel={() => setChannels((prev) => addChannel(prev, models))}
        showSidebar={showSidebar}
        onToggleSidebar={() => setShowSidebar((s) => !s)}
        showHistory={showHistory}
        onToggleHistory={() => setShowHistory((h) => !h)}
        showHistoryButton={HISTORY_ENABLED}
        canCompareModels={showComparison}
        classic={classicLayout}
      />

      <div className="flex min-h-0 flex-1">
        {HISTORY_ENABLED && showHistory && (
          <HistorySidebar
            onOpen={(id) => void openThread(id)}
            refreshSignal={historyRefresh}
            activeConversationId={activeConversationId}
          />
        )}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {authLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-maven-gray-100 dark:border-maven-gray-800 border-t-maven-teal-500" />
            </div>
          ) : authError ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="text-sm text-red-600">Auth error: {authError}</div>
            </div>
          ) : assistantType !== "spaces" && clientAccess === "loading" ? (
            <div className="flex flex-1 items-center justify-center text-sm text-maven-gray-500">
              Checking client access…
            </div>
          ) : assistantType !== "spaces" && clientAccess === "unavailable" ? (
            <div className="flex flex-1 items-center justify-center p-4">
              <div
                role="alert"
                className="max-w-md rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-700 dark:bg-amber-950"
              >
                <p className="mb-1 font-semibold text-amber-800 dark:text-amber-300">Client access check unavailable</p>
                <p className="text-amber-900 dark:text-amber-200">
                  We couldn&apos;t confirm which clients you have access to, so this assistant is paused. Reload the
                  page to try again, or switch to Spaces &amp; LMS.
                </p>
              </div>
            </div>
          ) : showFilterPanel ? (
            assistantType === "spaces" ? (
              <SpacesFilterPanel
                spacesFilters={spacesFilters}
                onBack={() => setShowFilterPanel(false)}
                onApply={() => setShowFilterPanel(false)}
              />
            ) : (
              <MetadataFilterPanel
                metadataFilters={metadataFilters}
                excludedClientIds={excludedClientIds}
                onBack={() => setShowFilterPanel(false)}
                onApply={() => {
                  metadataFilters.applyFilters();
                  setShowFilterPanel(false);
                }}
              />
            )
          ) : (
            <>
              {!multi && !classicLayout && (
                <div className="px-4 pt-3 text-xs text-maven-gray-500 dark:text-maven-gray-500">
                  Hi {userName} — ask anything, or add a model to compare answers.
                </div>
              )}

              <div className="flex min-h-0 flex-1">
                {channels.map((channel) => (
                  <AssistantChannel
                    key={channel.id}
                    channelId={channel.id}
                    modelId={channel.modelId}
                    models={models}
                    setModelId={(id) => setChannels((prev) => setChannelModel(prev, channel.id, id))}
                    onRemove={multi ? () => setChannels((prev) => removeChannel(prev, channel.id)) : undefined}
                    authToken={authToken}
                    userEmail={userEmail}
                    assistantType={assistantType}
                    fileSearchFilters={fileSearchFilters}
                    registerSend={registerSend}
                    onTurnComplete={recordTurn}
                    soloed={!multi}
                    showHeader={!classicLayout}
                    emptyState={
                      classicLayout ? (
                        <div className="flex h-full flex-col items-center justify-center text-maven-gray-500 dark:text-maven-gray-500">
                          <p className="text-lg font-medium">Hi {userName}!</p>
                          <p className="text-sm">How can I help?</p>
                        </div>
                      ) : undefined
                    }
                    restoredMessages={restored[channel.id]}
                  />
                ))}
              </div>

              {/* One composer for every channel */}
              <div className="border-t border-maven-gray-100 dark:border-maven-gray-800 bg-white dark:bg-maven-gray-800 p-3">
                {classicLayout && (
                  <div className="mb-2 flex gap-1 rounded-lg bg-gray-100 dark:bg-maven-gray-950 p-1">
                    {CORPUS_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setAssistantType(option.value)}
                        className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                          assistantType === option.value
                            ? "bg-maven-teal-500 text-white"
                            : "text-gray-600 dark:text-maven-gray-300 hover:bg-gray-200 dark:hover:bg-maven-gray-800"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}

                {classicLayout && assistantType === "projects" && (
                  <div className="mb-2 flex gap-1 rounded-lg bg-gray-50 dark:bg-maven-gray-950 p-1">
                    {([
                      { label: "General Question", value: "general" as const },
                      { label: "PAAB Rebuttal", value: "rebuttal" as const },
                    ]).map((mode) => (
                      <button
                        key={mode.value}
                        onClick={() => setProjectInputMode(mode.value)}
                        className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                          projectInputMode === mode.value
                            ? "bg-white dark:bg-maven-gray-800 text-gray-900 dark:text-maven-gray-100 shadow-sm"
                            : "text-gray-500 dark:text-maven-gray-300 hover:text-gray-700 dark:hover:text-white"
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                )}
                {classicLayout && assistantType === "projects" && projectInputMode === "rebuttal" ? (
                  <div className="space-y-3 rounded-lg border border-maven-gray-100 dark:border-maven-gray-800 bg-white dark:bg-maven-gray-800 p-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-maven-gray-300">
                        Verbatim PAAB Comment
                      </label>
                      <textarea
                        value={paabComment}
                        onChange={(e) => setPaabComment(e.target.value)}
                        placeholder="Paste the exact PAAB reviewer comment here..."
                        className="max-h-[100px] min-h-[60px] w-full resize-none rounded-md border border-gray-200 dark:border-maven-gray-800 bg-gray-50 dark:bg-maven-gray-950 p-2 text-sm focus:border-maven-teal-500 focus:outline-none focus:ring-1 focus:ring-maven-teal-500"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-maven-gray-300">
                        Verbatim Copy Deck Text
                      </label>
                      <textarea
                        value={copyDeckText}
                        onChange={(e) => setCopyDeckText(e.target.value)}
                        placeholder="Paste the exact copy deck text that PAAB is questioning..."
                        className="max-h-[100px] min-h-[60px] w-full resize-none rounded-md border border-gray-200 dark:border-maven-gray-800 bg-gray-50 dark:bg-maven-gray-950 p-2 text-sm focus:border-maven-teal-500 focus:outline-none focus:ring-1 focus:ring-maven-teal-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <SpacesFilterButton onClick={() => setShowFilterPanel(true)} activeFilterCount={activeFilterCount} />
                      <button
                        onClick={submitRebuttal}
                        disabled={!paabComment.trim() || !copyDeckText.trim()}
                        className="rounded-md bg-maven-teal-500 px-4 py-1.5 text-sm font-medium text-white hover:bg-maven-teal-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-maven-gray-950 dark:disabled:text-maven-gray-500"
                      >
                        Generate Rebuttal
                      </button>
                    </div>
                  </div>
                ) : (
                <div className="flex items-end gap-2 rounded-lg border border-maven-gray-100 dark:border-maven-gray-800 bg-white dark:bg-maven-gray-800 p-2">
                  <SpacesFilterButton onClick={() => setShowFilterPanel(true)} activeFilterCount={activeFilterCount} />
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        broadcast();
                      }
                    }}
                    placeholder={multi ? `Ask all ${channels.length} models…` : "Write your question here…"}
                    rows={1}
                    className="max-h-[120px] min-h-[36px] flex-1 resize-none border-0 bg-transparent p-2 text-sm focus:outline-none focus:ring-0"
                  />
                  <button
                    onClick={broadcast}
                    disabled={!draft.trim()}
                    className="rounded-md bg-maven-teal-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-maven-teal-700 disabled:cursor-not-allowed disabled:bg-maven-gray-100 dark:disabled:bg-maven-gray-950 disabled:text-maven-gray-500"
                  >
                    Send
                  </button>
                </div>
                )}
              </div>
            </>
          )}
        </div>

        {showComparison && showSidebar && <AssistantRail entries={log} onClearLog={() => setLog([])} />}
      </div>
    </div>
  );
}

